/**
 * Thin wrapper around `POST /api/UploadImage`.
 *
 * Web flow (different from mobile/iOS — see docs/transaction-images-ui.md):
 *   1. User picks a file in the form.
 *   2. We upload it immediately to the server.
 *   3. Server resizes, re-encodes (JPEG q=0.9 / PNG), saves to disk and returns
 *      `{ id, guid, fileName, version }`.
 *   4. Client renders the thumbnail directly from the server URL — no client-side
 *      compression, no base64, no Blob URL after upload.
 *
 * The transaction itself is saved later (PUT /api/transactions/...) with
 * `images: [{ id, guid, fileName }, ...]`.
 */

const ALLOWED_MIME = new Set(["image/jpeg", "image/png"]);
const ALLOWED_EXT = /\.(jpe?g|png)$/i;

export const ACCEPTED_IMAGE_MIME = "image/jpeg,image/png";

export const isAcceptedImageFile = (file) => {
    if (!file) return false;
    if (file.type && ALLOWED_MIME.has(file.type)) return true;
    return ALLOWED_EXT.test(file.name || "");
};

/**
 * Build the URL for an `<img src>`. The path filename is cosmetic — the server
 * resolves the file purely by id + current version — but we still pass the real
 * filename so it shows up in browser devtools / "Save image as…".
 *
 * We strip anything outside `[a-z0-9._-]` ourselves before encoding, mirroring
 * the server's sanitizer. This:
 *   - guarantees we never produce '%25' in the URL (Spring Security firewall block),
 *   - is idempotent if the server returns an already-encoded legacy filename,
 *   - keeps DevTools / "Save as…" with a meaningful name.
 */
export const buildImageUrl = (id, fileName) => {
    let raw = (fileName || "").toString().trim();
    if (raw) {
        // If the value contains a literal '%' it is almost certainly leftover from a
        // double-encoded legacy row — try to decode once, otherwise keep as-is.
        if (raw.indexOf("%") >= 0) {
            try { raw = decodeURIComponent(raw); } catch (_) { /* keep raw */ }
        }
    }
    const safe = raw.toLowerCase().replace(/[^a-z0-9._-]+/g, "_").replace(/_+/g, "_") || "image";
    return `/api/image/${id}/${encodeURIComponent(safe)}`;
};

/**
 * Upload a single image. Resolves with `{ id, guid, fileName, version, status }`
 * on success, rejects with an Error otherwise.
 *
 * The server will downscale > 1024px on the long side and re-encode (no need to
 * compress on the client).
 */
export const uploadTransactionImage = async (file) => {
    if (!isAcceptedImageFile(file)) {
        throw new Error("Unsupported image format (only JPEG and PNG are allowed).");
    }
    const form = new FormData();
    form.append("file", file, file.name || "upload.jpg");
    form.append("type", "TRANSACTION");
    form.append("version", "0");

    const response = await fetch("/api/UploadImage", {
        method: "POST",
        body: form,
    });
    if (!response.ok) {
        throw new Error(`Upload failed (HTTP ${response.status}).`);
    }
    const json = await response.json();
    if (!json || (json.status && json.status !== "OK" && json.status !== "CONFLICT")) {
        throw new Error(`Upload failed (status=${json && json.status}).`);
    }
    if (json.id == null) {
        // Server is older than the patch that adds `id` to UploadImageRs.
        throw new Error("Server response is missing image id.");
    }
    return {
        id: json.id,
        guid: json.guid,
        fileName: json.fileName,
        version: json.version,
        status: json.status,
    };
};

const ImageUploadService = {
    uploadTransactionImage,
    buildImageUrl,
    isAcceptedImageFile,
    ACCEPTED_IMAGE_MIME,
};

export default ImageUploadService;
